import { nextTick, type ComputedRef, type Ref } from 'vue'
import type { useSourceToast } from '~/composables/ui/useSourceToast'
import {
  cloneReorderGroups,
  dirtyReorderGroups,
  isReorderPermutation,
  keyboardDestinationIndex,
  moveReorderItem,
  replaceGroupKeys,
  type ReorderGroup,
} from '~/composables/orga/reorder/reorderModel'
import type { OrgGridReorderOptions } from '~/composables/orga/reorder/orgGridReorderTypes'
import type { useOrgReorderSortable } from '~/composables/orga/reorder/useOrgReorderSortable'

type SortableApi = ReturnType<typeof useOrgReorderSortable>

export type ReorderSession = {
  options: OrgGridReorderOptions
  toast: ReturnType<typeof useSourceToast>
  isReordering: Ref<boolean>
  saving: Ref<boolean>
  announcement: Ref<string>
  initialGroups: Ref<ReorderGroup[]>
  pendingPath: Ref<string | null>
  closeEditAfterDiscard: Ref<boolean>
  allowLeave: Ref<boolean>
  sortable: SortableApi
  isDirty: ComputedRef<boolean>
  setEditing: (value: boolean) => void
  requestClose: () => void
}

function errorText(error: unknown): string {
  if (error instanceof Error && error.message.trim() !== '') {
    return error.message
  }
  return 'L’enregistrement de l’ordre a échoué.'
}

function stopReorder(session: ReorderSession): void {
  session.isReordering.value = false
  session.announcement.value = ''
  session.sortable.destroy()
}

function restoreInitial(session: ReorderSession): void {
  session.options.writeGroups(cloneReorderGroups(session.initialGroups.value))
}

export function requestCancel(session: ReorderSession): void {
  session.pendingPath.value = null
  session.closeEditAfterDiscard.value = false
  session.requestClose()
}

export async function startReorder(session: ReorderSession): Promise<void> {
  if (!session.options.enabled.value) {
    return
  }
  if (!session.isReordering.value) {
    session.initialGroups.value = cloneReorderGroups(session.options.readGroups())
    session.announcement.value = ''
  }
  session.isReordering.value = true
  await session.sortable.bind()
}

export function cancelDraft(session: ReorderSession): void {
  restoreInitial(session)
  session.announcement.value = ''
  void session.sortable.bind()
}

export async function saveReorder(session: ReorderSession): Promise<void> {
  if (!session.isDirty.value || session.saving.value) {
    return
  }
  session.saving.value = true
  try {
    await session.options.persist(
      dirtyReorderGroups(session.options.readGroups(), session.initialGroups.value),
    )
    session.toast.success('Ordre enregistré', session.options.successMessage)
    await session.options.reload()
    session.initialGroups.value = cloneReorderGroups(session.options.readGroups())
    session.announcement.value = ''
    if (session.options.enabled.value) {
      session.isReordering.value = true
      await session.sortable.bind()
    } else {
      stopReorder(session)
    }
  } catch (error) {
    session.toast.error('Enregistrement impossible', errorText(error))
  } finally {
    session.saving.value = false
  }
}

export function applyDomOrder(session: ReorderSession, groupId: string, keys: string[]): void {
  const current = session.options.readGroups()
  const next = replaceGroupKeys(current, groupId, keys)
  if (!isReorderPermutation(current, next)) {
    return
  }
  session.options.writeGroups(next)
  void session.sortable.bind()
}

function announceMove(label: string, position: number, total: number): string {
  return `${label} déplacée en position ${position} sur ${total}`
}

async function focusMovedHandle(
  session: ReorderSession,
  groupId: string,
  movedKey: string | undefined,
): Promise<void> {
  await nextTick()
  await session.sortable.bind()
  const root = session.sortable.rootRef.value
  if (!movedKey || !root) {
    return
  }
  const handle = root.querySelector(`[data-reorder-handle="${CSS.escape(`${groupId}:${movedKey}`)}"]`)
  if (handle instanceof HTMLElement) {
    handle.focus()
  }
}

export function moveByKeyboard(
  session: ReorderSession,
  groupId: string,
  keyName: string,
  fromIndex: number,
): void {
  const group = session.options.readGroups().find(function sameGroup(candidate) {
    return candidate.id === groupId
  })
  if (!group) {
    return
  }
  const destination = keyboardDestinationIndex(
    group.keys.length,
    fromIndex,
    keyName,
    session.options.axis,
  )
  if (destination === null || destination === fromIndex) {
    return
  }
  const movedKey = group.keys[fromIndex]
  session.options.writeGroups(
    moveReorderItem(session.options.readGroups(), groupId, fromIndex, destination),
  )
  session.announcement.value = announceMove(movedKey ?? groupId, destination + 1, group.keys.length)
  void focusMovedHandle(session, groupId, movedKey)
}

export function discardReorder(session: ReorderSession): void {
  restoreInitial(session)
  const leaving = session.closeEditAfterDiscard.value || session.pendingPath.value !== null
  if (leaving) {
    stopReorder(session)
  } else {
    session.announcement.value = ''
    void session.sortable.bind()
  }
  if (session.closeEditAfterDiscard.value) {
    session.closeEditAfterDiscard.value = false
    session.setEditing(false)
  }
  const path = session.pendingPath.value
  session.pendingPath.value = null
  if (path) {
    session.allowLeave.value = true
    void navigateTo(path)
  }
}

export function onEditModeChange(session: ReorderSession, editing: boolean): void {
  if (editing || !session.isReordering.value) {
    return
  }
  if (!session.isDirty.value) {
    stopReorder(session)
    return
  }
  session.closeEditAfterDiscard.value = true
  session.setEditing(true)
  session.requestClose()
}

export function onEscapeKey(session: ReorderSession, event: KeyboardEvent): void {
  if (event.key !== 'Escape' || !session.isReordering.value || !session.isDirty.value) {
    return
  }
  event.preventDefault()
  requestCancel(session)
}
